/// <reference path="../types.d.ts" />
import history from '../history';
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../core/net';
import { DictionaryValues, DictionaryUpload, ColumnMetadataEditor, ArchiveModal } from './action-types';
import { showFeedback, dismissFeedback, showFeedbackError, showFeedbackWarning } from './ui';
import { filterStatusFields } from '../reducers/state/dictionary-values';
import { ActionToast } from '../views/ui/action-toast';
import { commonEnums, HttpMethods, cmxErrors } from '@codametrix/ui-common';
import { isNewDesign } from '../core/middleware/new-design-middleware';
import { cmxDateTime, AmplifyCore } from '@codametrix/ui-common';
import { savePotentialArchives, selectArchiveRow, toggleArchiveModal } from './dictionary-archive-row.actions';
const { paginationUtils } = AmplifyCore;
const { BadRequestError } = cmxErrors;
const { DictionaryStatus, FilterTypes } = commonEnums;
const chooseRow = createAction(DictionaryValues.CHOOSE_ROW);
const chooseColumnReference = createAction(DictionaryUpload.CHOOSE_COL_REFERENCE);
const removeColumnMetadata = createAction(DictionaryUpload.REMOVE_COLUMN_METADATA);
const setStatusFilter = createAction(DictionaryValues.SET_STATUS_FILTER);
const selectKeyColumn = createAction(ColumnMetadataEditor.SELECT_KEY_COLUMN);
const confirmMappingsExist = createAction(ColumnMetadataEditor.CONFIRM_MAPPINGS_EXISTS);
const setDictionaryMappings = createAction(ColumnMetadataEditor.SET_DICTIONARY_MAPPINGS);
const selectActiveColumn = createAction(ColumnMetadataEditor.SELECT_ACTIVE_COLUMN);
const removeColumnMapping = createAction(ColumnMetadataEditor.REMOVE_COLUMN_MAPPING);
const saveColumnMapping = createAction(ColumnMetadataEditor.SAVE_COLUMN_MAPPING);
const setArchiveChildren = createAction(ArchiveModal.ARCHIVE_CHILDREN);
const toggleDeleteModal = createAction(DictionaryValues.TOGGLE_DELETE_MODAL);
const togglePublishModal = createAction(DictionaryValues.TOGGLE_PUBLISH_MODAL);
const toggleUploadType = createAction(DictionaryUpload.TOGGLE_UPLOAD_TYPE);
const toggleInfoModal = createAction(DictionaryValues.TOGGLE_INFO_MODAL);
const toggleSecurityModal = createAction(DictionaryValues.TOGGLE_SECURITY_MODAL);
const updateDictionaryValue = createAction(DictionaryValues.UPDATE_COLUMN_DEFS);
//We want to use async so  we can get a loadstate change to cause a rerender on the form component
const initUpload = createAsyncThunk(DictionaryUpload.UPLOAD_INIT, async ()=>{
    return;
});
/** async to reload form, thus allowing the forms state to refresh */ const addColumnMetadata = createAsyncThunk(DictionaryUpload.ADD_COLUMN_METADATA, async (value, { dispatch })=>{
    return value;
});
const loadValue = createAsyncThunk(DictionaryValues.LOAD_VALUE, async (params, { dispatch })=>{
    const { path, data: rowId } = params;
    history.push(path + `/row/${rowId}/edit`);
    return rowId;
});
export const shimDraftFilter = (filters)=>{
    const shimmedFilters = filters.filter((filter)=>![
            'edit_status',
            'status'
        ].includes(filter.key));
    shimmedFilters.push({
        key: 'status',
        terms: [
            'draft',
            'published'
        ],
        type: 'IN'
    }, {
        key: 'edit_status',
        terms: [
            'ADD',
            'UPDATE',
            null
        ],
        type: 'IN'
    });
    return shimmedFilters;
};
const getDraftFilters = async (filters, dictionaryId)=>{
    const isDraft = filters.filter((f)=>{
        const isEqual = f.terms.includes('draft');
        return isEqual;
    }).length;
    if (!isDraft) return null;
    const deleteAndUpdateRowSearch = api({
        endpoint: `/dictionary/${dictionaryId}/row/v2?page=0&size=1&listType=column`,
        init: {
            method: HttpMethods.POST
        },
        body: [
            {
                key: 'status',
                terms: [
                    'published'
                ],
                type: 'IN'
            },
            {
                key: 'edit_status',
                terms: [
                    'DELETE',
                    'UPDATE'
                ],
                type: 'IN'
            }
        ]
    });
    const draftRowSearch = api({
        endpoint: `/dictionary/${dictionaryId}/row/v2?page=0&size=1&listType=column`,
        init: {
            method: HttpMethods.POST
        },
        body: [
            {
                key: 'status',
                terms: [
                    'draft'
                ],
                type: 'IN'
            }
        ]
    });
    const [deletedAndUpdateRows, draftRows] = await Promise.all([
        deleteAndUpdateRowSearch,
        draftRowSearch
    ]);
    if (deletedAndUpdateRows?.content.length !== 0 || draftRows?.content.length !== 0) {
        const shimmedDraftFilters = shimDraftFilter(filters);
        return shimmedDraftFilters;
    }
    return null;
};
export const getDictionaryFilters = async (dictionaryId, sortablePageable)=>{
    const { filterableOptions: { filters = [] } } = sortablePageable;
    const statusTerm = filters.find((f)=>f.key === 'status')?.terms[0];
    // Filtering for Deleted Rows, Added Rows, Or updated rows requires shimming in an editStatus filter.
    const shimEditStatusFilter = filterStatusFields.find((field)=>field.key === statusTerm)?.transform;
    let shimmedFilters = filters;
    if (shimEditStatusFilter) {
        shimmedFilters = shimEditStatusFilter(filters);
    }
    let shimmedDraftFilters = shimmedFilters;
    const draftFilters = await getDraftFilters(filters, dictionaryId);
    if (draftFilters) shimmedDraftFilters = draftFilters;
    return shimmedDraftFilters;
};
const checkIfKeyColumnPresent = (dictionary, dispatch)=>{
    const hasKeyColumn = dictionary.columnDefinitions.find((col)=>{
        return col.keyColumnIndicator === true;
    }) !== undefined;
    if (!hasKeyColumn) {
        dispatch(showFeedbackWarning({
            message: ActionToast({
                actionText: 'here',
                message: `Key column not set. Please select one`,
                onAction: (path)=>{
                    history.push(`${path}/metadata/keyColumn`);
                    dispatch(dismissFeedback(true));
                }
            }),
            meta: {
                level: 'WARNING'
            },
            dismissable: true,
            className: 'feedback-error'
        }));
    }
};
/**
 * Listing draft rows may not include all the rows of a draft dictionary.
 * Some may be a part of the existing published dictionary.
 * Therefore we need to check to see if any rows exist in the published dictionary with editStatus ADD/UPDATE/DELETE
 **/ const listItems = createAsyncThunk(DictionaryValues.LIST_VALUES, async (params, { dispatch })=>{
    const { dictionary, sortablePageable } = params;
    var searchParams = paginationUtils.searchParamsFactory(sortablePageable);
    const filters = await getDictionaryFilters(dictionary.id, sortablePageable);
    let pageableList = await api({
        endpoint: `/dictionary/${dictionary.id}/row/v2?${searchParams.toString()}&listType=column`,
        init: {
            method: HttpMethods.POST
        },
        body: filters || []
    });
    return pageableList;
});
const loadDictionary = createAsyncThunk(DictionaryValues.LOAD_DICTIONARY, async (params, { dispatch })=>{
    dispatch(showFeedback({
        id: Date.now(),
        message: `Loading dictionary values...`,
        dismissable: false,
        className: 'feedback-working'
    }));
    const { id, sortablePageable } = params;
    const dictionary = await api({
        endpoint: `/dictionary/${id}/filters/v2?pageSize=0`,
        init: {
            method: HttpMethods.POST
        },
        body: []
    });
    dispatch(initializeColumnMetadataEditor(dictionary));
    !isNewDesign && dispatch(listItems({
        dictionary,
        sortablePageable
    }));
    checkIfKeyColumnPresent(dictionary, dispatch);
    dispatch(dismissFeedback(true));
    return dictionary;
});
const saveDictionaryRow = createAsyncThunk(DictionaryValues.SAVE_VALUE, async (value, { dispatch })=>{
    const endpoint = `/dictionary/row/v2`;
    const res = await api({
        endpoint: endpoint,
        init: {
            method: value.id ? HttpMethods.PUT : HttpMethods.POST
        },
        body: value
    });
    return res;
});
const listColumnValues = createAsyncThunk(DictionaryValues.LIST_COLUMN_VALUES, async (value, { dispatch })=>{
    const { cellReferences, tenantId } = value;
    const { dictionaryName, lookupProperty: columnName } = cellReferences[0];
    // getting the dictionary id from dictionary name and tenant id
    const dictionaryEndpoint = `/dictionary/${dictionaryName}/list/v2?tenantId=${tenantId.uuid}`;
    const dictionary = await api({
        endpoint: dictionaryEndpoint,
        init: {
            method: HttpMethods.GET
        }
    });
    const endpoint = `/dictionary/${dictionary[0].id}/cell/v2?page=0&size=1000&listType=column`;
    const res = await api({
        endpoint: endpoint,
        init: {
            method: HttpMethods.POST
        },
        body: [
            {
                key: columnName,
                terms: [
                    ''
                ],
                type: 'LIKE'
            }
        ]
    });
    return res.content;
});
const listDictionaryOptions = createAsyncThunk(DictionaryUpload.LIST_DICTIONARIES, async (params, { dispatch })=>{
    const pageableList = await api({
        endpoint: `/dictionary/v2?tenantId=${params.tenantId}&sort=name%2CASC&size=1000&&derivedIndicator=false&uniqueByName=true&status=${DictionaryStatus.PUBLISHED.toUpperCase()}`,
        init: {
            method: HttpMethods.GET
        }
    });
    return {
        pageableList: pageableList,
        organizationName: params.orgName
    };
});
const listColumnOptions = createAsyncThunk(DictionaryUpload.LIST_COLUMNS, async (params, { dispatch })=>{
    const dateStr = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
    const pageableList = await api({
        endpoint: `/dictionary/${params.dictionaryName}/column-definition/v2?derivedIndicator=false&effectiveDateStr=${dateStr}&tenantId=${params.tenantId}`,
        init: {
            method: HttpMethods.GET
        }
    });
    return pageableList;
});
//export for testing
export const getPublishableDictionaryIds = (associatedDictionaries)=>{
    const publishedDictionaryId = associatedDictionaries.find((dict)=>{
        return dict.status === DictionaryStatus.PUBLISHED.toUpperCase();
    })?.id;
    let draftDictionaryId = associatedDictionaries.find((dict)=>{
        return dict.status === DictionaryStatus.DRAFT.toUpperCase();
    })?.id;
    if (publishedDictionaryId && draftDictionaryId) {
        return [
            draftDictionaryId,
            publishedDictionaryId
        ];
    }
    if (publishedDictionaryId && !draftDictionaryId) {
        return [
            publishedDictionaryId,
            undefined
        ];
    }
    return [
        draftDictionaryId,
        undefined
    ];
};
const publishDictionary = createAsyncThunk(DictionaryValues.PUBLISH_DICTIONARY, async (params, { dispatch })=>{
    const { assocDictionaries, jiraTicket } = params;
    const dateStr = cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE);
    const [recentDictionaryId, optOldDictionaryId] = getPublishableDictionaryIds(assocDictionaries);
    let searchParams = new URLSearchParams();
    searchParams.set('effectiveDateStr', dateStr);
    searchParams.set('jiraTicket', jiraTicket);
    if (optOldDictionaryId) {
        searchParams.set('oldDictionaryId', optOldDictionaryId.toString());
    }
    const uploadResponse = await api({
        endpoint: `/dictionary/${recentDictionaryId}/publish/v2?${searchParams.toString()}`,
        init: {
            method: HttpMethods.PUT
        }
    }).catch((e)=>{
        if (e instanceof BadRequestError) {
            dispatch(dismissFeedback(false));
            dispatch(showFeedbackError({
                id: Date.now(),
                message: `Dictionary Publish Unsuccessful`,
                dismissable: true
            }));
        }
        throw e;
    });
    const successRoute = window.location.pathname.split('/').slice(0, 6).join('/');
    const navParams = new URLSearchParams({
        requestId: uploadResponse.id.toString()
    });
    history.push(successRoute + `/request?${navParams}`);
    return uploadResponse;
});
const downloadDictionaryByRequestID = createAsyncThunk(DictionaryValues.DOWNLOAD_DICTIONARY_BY_REQUEST_ID, async (payload, { dispatch })=>{
    const { requestId } = payload;
    dispatch(showFeedback({
        id: Date.now(),
        message: `Downloading Dictionary. Please Wait`,
        dismissable: true,
        className: 'feedback-working'
    }));
    const endpoint = `/dictionary/request/${requestId}/csv/v2`;
    const response = await api({
        endpoint,
        headers: {
            'Content-Type': 'application/csv'
        }
    }, false).catch((e)=>{
        dispatch(dismissFeedback(false));
        dispatch(showFeedbackError({
            id: Date.now(),
            message: `Dictionary Download Unsuccessful`,
            dismissable: true
        }));
    });
    const filename = response.contentDisposition?.split('=')[1];
    var link = document.createElement('a');
    if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        var url = URL.createObjectURL(response.blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    dispatch(showFeedback({
        id: Date.now(),
        message: `Dictionary Download Finished`,
        dismissable: true,
        className: 'feedback-working'
    }));
});
const downloadDictionary = createAsyncThunk(DictionaryValues.DOWNLOAD_DICTIONARY, async (payload, { dispatch })=>{
    const { dictionaryName, tenantId, effectiveDate, updatedDate, withDraft } = payload;
    let searchParams = new URLSearchParams();
    searchParams.append('tenantId', tenantId);
    if (withDraft) {
        searchParams.append('withDraft', 'true');
    }
    if (effectiveDate) {
        searchParams.append('effectiveDateStr', updatedDate ? updatedDate : cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE));
    }
    dispatch(showFeedback({
        id: Date.now(),
        message: `Downloading Dictionary. Please Wait`,
        dismissable: true,
        className: 'feedback-working'
    }));
    const endpoint = `/dictionary/${encodeURI(dictionaryName)}/csv/v2?${searchParams.toString()}`;
    const response = await api({
        endpoint,
        headers: {
            'Content-Type': 'application/csv'
        }
    }, false).catch((e)=>{
        dispatch(dismissFeedback(false));
        dispatch(showFeedbackError({
            id: Date.now(),
            message: `Dictionary Download Unsuccessful`,
            dismissable: true
        }));
    });
    const filename = response.contentDisposition?.split('=')[1];
    var link = document.createElement('a');
    if (link.download !== undefined) {
        // feature detection
        // Browsers that support HTML5 download attribute
        var url = URL.createObjectURL(response.blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
    dispatch(showFeedback({
        id: Date.now(),
        message: `Dictionary Download Finished`,
        dismissable: true,
        className: 'feedback-working'
    }));
});
const listAssociatedDictionaries = createAsyncThunk(DictionaryValues.LIST_ASSOCIATED_DICTIONARIES, async (dictionary, { dispatch })=>{
    const searchParams = new URLSearchParams();
    searchParams.set('tenantId', dictionary.tenantId.uuid);
    const dictionaries = await api({
        endpoint: `/dictionary/${dictionary.name}/list/v2?${searchParams.toString()}`
    }) || [];
    return dictionaries;
});
/**
 * Async function to get history of a dictionary
 * @param DictionaryValues.LIST_DICTIONARY_HISTORY
 * @param <Function>
 * @retuns
 */ const listDictionaryHistory = createAsyncThunk(DictionaryValues.LIST_DICTIONARY_HISTORY, async (dictionaryId, { dispatch })=>{
    // default row params
    const defaultRowParams = new URLSearchParams({
        page: '0',
        size: '200'
    });
    // create necessary params
    const rowBody = [
        {
            key: 'dictionary_id',
            terms: [
                dictionaryId
            ],
            type: FilterTypes.EQ
        }
    ];
    // request to API
    const history = await api({
        endpoint: `/dictionary/request/v2?${defaultRowParams.toString()}`,
        init: {
            method: HttpMethods.POST
        },
        body: rowBody
    }).catch((e)=>{
        if (e instanceof BadRequestError) {
            dispatch(dismissFeedback(false));
            dispatch(showFeedbackError({
                id: Date.now(),
                message: `Getting History Dictionary Unsuccessful`,
                dismissable: true
            }));
        }
        throw e;
    });
    return history.content;
});
const initializeColumnMetadataEditor = createAsyncThunk(ColumnMetadataEditor.INITIALIZE, async (dictionary, { dispatch })=>{
    const searchParams = new URLSearchParams();
    searchParams.append('tenantId', dictionary.tenantId.uuid);
    searchParams.append('derivedIndicator', 'false');
    if (dictionary.effectiveDate && dictionary.status !== 'PUBLISHED') {
        searchParams.append('effectiveDateStr', dictionary.effectiveDate);
    } else if (dictionary.status !== 'DRAFT') {
        searchParams.append('effectiveDateStr', dictionary.updatedDate ?? cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE));
    }
    const cols = await api({
        endpoint: `/dictionary/${dictionary.name}/column-definition/v2?${searchParams.toString()}`
    });
    const hasKeyColumn = cols?.some((col)=>col.keyColumnIndicator === true);
    if (!hasKeyColumn && cols?.length > 0) {
        cols[0].keyColumnIndicator = true;
    }
    return cols;
});
const saveColumnDefinitions = createAsyncThunk(ColumnMetadataEditor.SAVE_COLUMN_METADATA, async (payload, { dispatch })=>{
    //Column definition save call
    const { columnDefinitions, succesPath, dictionaryId } = payload;
    const res = await api({
        endpoint: `/dictionary/${dictionaryId}/column-definition/v2`,
        init: {
            method: HttpMethods.PUT
        },
        body: columnDefinitions
    });
    if (res && res.status === 400) {
        dispatch(showFeedbackError({
            id: Date.now(),
            message: `Metadata Save Unsuccessful!`,
            dismissable: true
        }));
        throw res;
    }
    const updatedDictionary = await api({
        endpoint: `/dictionary/${dictionaryId}/filters/v2?pageSize=0`,
        init: {
            method: HttpMethods.POST
        },
        body: []
    });
    history.push(succesPath);
    dispatch(showFeedback({
        id: Date.now(),
        message: `Metadata saved succesfully`,
        dismissable: true,
        className: 'feedback-working'
    }));
    return updatedDictionary;
});
const archiveRow = createAsyncThunk(ArchiveModal.ARCHIVE_ROW, async (payload, { dispatch })=>{
    const { row, archiveChildren, dictionary, sortablePageable, jiraTicket } = payload;
    const keyValue = row.cells.find((cell)=>cell.columnDefinition.keyColumnIndicator)?.value;
    let archivedRows = [];
    if (archiveChildren && keyValue && jiraTicket) {
        const searchParams = new URLSearchParams({
            endDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
            rowKey: keyValue,
            tenantId: row.tenantId.uuid,
            jiraTicket
        });
        archivedRows = await api({
            endpoint: `/dictionary/${row.dictionaryName}/row/archive/v2?${searchParams.toString()}`,
            init: {
                method: HttpMethods.PUT
            }
        });
    }
    const searchParams = new URLSearchParams({
        endDate: cmxDateTime.format(new Date(), cmxDateTime.FORMATS.DATE),
        jiraTicket
    });
    const archivedRow = await api({
        endpoint: `/dictionary/row/${row.id}/archive/v2?${searchParams.toString()}`,
        init: {
            method: HttpMethods.PUT
        }
    });
    dispatch(listItems({
        dictionary,
        sortablePageable
    }));
    return [
        archivedRow,
        ...archivedRows
    ];
});
const deleteDictionary = createAsyncThunk(DictionaryValues.DELETE_DICTIONARY, async (deleteIds, { dispatch })=>{
    const deleteResponses = await Promise.all(deleteIds.map((id)=>{
        return api({
            endpoint: `/dictionary/${id}/v2`,
            init: {
                method: HttpMethods.DELETE
            }
        }) || false;
    })).catch((e)=>{
        if (e instanceof BadRequestError) {
            dispatch(dismissFeedback(false));
            dispatch(showFeedbackError({
                id: Date.now(),
                message: `Dictionary Delete Unsuccessful`,
                dismissable: true
            }));
        }
        throw e;
    });
    const successRoute = window.location.pathname.split('/').slice(0, 6).join('/');
    let navParams = new URLSearchParams();
    deleteResponses.forEach((res)=>{
        navParams.append('requestId', res.id.toString());
    });
    history.replace(successRoute + `/request?${navParams.toString()}`);
    return false;
});
const updateDictionaryDescription = createAsyncThunk(DictionaryValues.UPDATE_DICTIONARY_DESCRIPTION, async (payload, { dispatch })=>{
    const updatedDictionary = await api({
        endpoint: `/dictionary/description/v2`,
        init: {
            method: HttpMethods.PUT
        },
        body: payload
    });
    return updatedDictionary;
});
const unrestrictDictionary = createAsyncThunk(DictionaryValues.UNRESTRICT_DICTIONARY, async (dictionaryId, { dispatch })=>{
    await api({
        endpoint: `/dictionary/${dictionaryId}/unrestricted/v2`,
        init: {
            method: HttpMethods.PUT
        }
    }).catch((e)=>{
        dispatch(dismissFeedback(false));
        dispatch(showFeedbackError({
            id: Date.now(),
            message: `Dictionary Unrestrict Unsuccessful`,
            dismissable: true
        }));
    });
});
const applyFilters = createAsyncThunk(DictionaryValues.SET_STATUS_FILTER, async (status, { dispatch })=>{
    dispatch(setStatusFilter(status));
    return status;
});
export const syncActions = {
    chooseRow,
    setStatusFilter,
    selectKeyColumn,
    confirmMappingsExist,
    setDictionaryMappings,
    selectActiveColumn,
    saveColumnMapping,
    removeColumnMapping,
    toggleArchiveModal,
    setArchiveChildren,
    savePotentialArchives,
    updateDictionaryValue
};
export { listItems, loadDictionary, loadValue, saveDictionaryRow, listDictionaryOptions, listColumnOptions, publishDictionary, addColumnMetadata, removeColumnMetadata, chooseColumnReference, initUpload, downloadDictionary, downloadDictionaryByRequestID, listAssociatedDictionaries, listDictionaryHistory, initializeColumnMetadataEditor, saveColumnDefinitions, selectArchiveRow, archiveRow, toggleDeleteModal, deleteDictionary, toggleUploadType, toggleInfoModal, toggleSecurityModal, togglePublishModal, unrestrictDictionary, updateDictionaryDescription, applyFilters, getDraftFilters, listColumnValues };
